// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import sharedActionCreators from "@modules/shared/actionCreator";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import * as sharedSelectors from "@modules/shared/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  LinearWizard,
  Step,
} from "@components";
import type { HierarchyAssignment } from "@modules/service/types";
import type { Alert } from "@modules/service/reducer/alerts/types";

import Step0AlertType from "./step-alert-type";
import StepAlertsCriteria from "./step-alerts-criteria";
import StepContent from "./step-content";
import StepAbsenceReasons from "./step-absence-reasons";
import StepRecipients from "./step-recipients";
import StepRestrictStaffGroups from "./step-restrict-staff-groups";
import StepServiceLength from "./step-service-length";
import Step7HierarchyScope from "./step-hierarchy-scope";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";

type OwnProps = $Exact<{
  showModal: boolean,
  defaultStep: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
  clientId: number,
  isActive?: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Alert,
  assignments: Array<HierarchyAssignment>,
  isAlertAssignmentsValid: boolean,
  hierarchy: ?HierarchyTree,
}>;

type PropsFromDispatch = $Exact<{
  add: typeof actionCreators.AddAlert,
  resetState: typeof actionCreators.ResetAlertsState,
  setAssignments: typeof actionCreators.SetAlertAssignments,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class AlertsAddModal extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    this.validate = new Ajv({ multipleOfPrecision: 2 }).compile(this.props.schema);
    const rootArea = this.getHierarchyRootAreaData;

    if (rootArea) {
      this.props.setAssignments([rootArea]);
    }
  }

  get getHierarchyRootAreaData() {
    const rootArea = this.props.hierarchy?.data;
    if (rootArea) {
      const activeFrom = new Date().toISOString();
      const data = {
        activeFrom,
        activeTo: null,
        isCascading: true,
        target: {
          id: rootArea.id,
          label: this.getNodeName(rootArea.id),
          type: "ORGANISATIONAL_UNIT",
        },
        exclusions: [],
      };

      return data;
    }

    return null;
  }

  getNodeName = (id: number, tree: HierarchyTree = this.props.hierarchy): string => {
    if (tree.data.id === id) {
      return tree.data.label;
    }

    return tree.data
      ? tree.children.reduce((acc: string, c: *) => acc || this.getNodeName(id, c), "")
      : "";
  };

  get isValid() {
    if (!this.validate || !this.props.draft) {
      return false;
    }

    const isValid = this.validate(removeNullAndUndefinedProps(this.props.draft?.content));
    if (!isValid) {
      console.warn("isValid errors", this.validate.errors);
    }

    return isValid;
  }

  get isAbsenceReasonsValid() {
    const alertType = this.props.draft?.content?.details?.alertType;
    if (alertType !== "ABSENCE_REASON_ALERT_TYPE") {
      return true;
    }

    const reasons = this.props.draft?.content?.details?.absenceReasonAlertType?.absenceReasons;
    return Array.isArray(reasons) && reasons.length;
  }

  handleSubmit = () => {
    this.props.add({
      clientId: this.props.clientId,
      draft: this.props.draft?.content,
      assignments: this.props.assignments,
      isActive: this.props.isActive,
    });
    this.props.resetState();
    this.props.onCancel();
  };

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.props.onCancel}>
          {this.props.t("ALERT_ADD_TITLE")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          <LinearWizard defaultStep={this.props.defaultStep} data={[]}>
            <Step component={Step0AlertType} title={this.props.t("ALERT_STEP0")} />
            <Step component={StepAlertsCriteria} title={this.props.t("ALERT_STEP1")} />
            <Step component={StepContent} title={this.props.t("ALERT_STEP2")} />
            <Step component={StepAbsenceReasons} title={this.props.t("ALERT_STEP3")} />
            <Step
              component={StepRecipients}
              title={this.props.t("ALERT_STEP4")}
              clientId={this.props.clientId}
            />
            <Step component={StepRestrictStaffGroups} title={this.props.t("ALERT_STEP5")} />
            <Step component={StepServiceLength} title={this.props.t("ALERT_STEP6")} />
            <Step component={Step7HierarchyScope} title={this.props.t("ALERT_STEP7")} />
          </LinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.onCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            disabled={
              !(this.isValid && this.props.isAlertAssignmentsValid && this.isAbsenceReasonsValid)
            }
            onClick={this.handleSubmit}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getAlertSchema(state),
    draft: serviceSelectors.getAlertDraft(state),
    assignments: serviceSelectors.getAlertAssignments(state),
    isAlertAssignmentsValid: serviceSelectors.isAlertAssignmentsValid(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  add: actionCreators.AddAlert,
  resetState: actionCreators.ResetAlertsState,
  setAssignments: actionCreators.SetAlertAssignments,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AlertsAddModal);
